import { useMessages } from '@src/@data/utility/hooks/useMessages';
import { TooltipLabel } from '@src/components/molecules/Tooltip';
import DatePicker from 'react-flatpickr';
import { useIntl } from 'react-intl';
import { Col, Label, Row } from 'reactstrap';
import {
  TimeSelector,
  TimeSelectorProps,
  TimeslotOption,
} from '../TimeSelector';
import React, { useMemo } from 'react';
import { isDateInRange } from '@src/@data/utility/dates';

export interface DateAndTimeRowData {
  id: number;
  timeslots?: TimeslotOption[];
  areTimeslotsLoading: boolean;
  startDate: Date;
  startTime: Date | undefined;
}

export interface DateAndTimeRowDataWithActions extends DateAndTimeRowData {
  setStartTime: (startTime: Date | undefined) => void;
  setStartDate: (date: Date) => void;
}

export interface DateAndTimeRowProps {
  row: Omit<DateAndTimeRowDataWithActions, 'id'>;
  areTimeslotsDisabled: boolean;
  menuPlacement?: TimeSelectorProps['menuPlacement'];
  isClientReservation?: boolean;
  dateChangeDisabled?: boolean;
  className?: string;
  selectedTimeslots?: Date[];
  duration?: number;
}

export const DateAndTimeRow: React.FC<DateAndTimeRowProps> = ({
  row: {
    timeslots,
    areTimeslotsLoading,
    setStartDate,
    setStartTime,
    startDate,
    startTime,
  },
  selectedTimeslots,
  areTimeslotsDisabled,
  menuPlacement,
  isClientReservation,
  dateChangeDisabled,
  className,
  duration,
}) => {
  const { messages } = useMessages();
  const intl = useIntl();

  const filteredTimeslots = useMemo(() => {
    if (!selectedTimeslots?.length || !timeslots?.length || !duration) {
      return timeslots;
    }
    const parsedSelectedTimeslots = selectedTimeslots.map((t) => t.getTime());
    const durationMs = duration * 60 * 1000;
    return timeslots.filter(({ value }) => {
      const dateMs = value.getTime();
      return parsedSelectedTimeslots.every(
        (startDateMs) =>
          !isDateInRange({
            dateMs,
            startDateMs: startDateMs - durationMs,
            endDateMs: startDateMs + durationMs,
          })
      );
    });
  }, [duration, selectedTimeslots, timeslots]);

  return (
    <Row noGutters className={className}>
      <Col className="pr-50" xs={{ size: 6 }}>
        <Label for="date-picker" className="text-secondary">
          {messages('label_date')}
        </Label>
        <DatePicker
          value={startDate}
          id="date-picker"
          className="form-control"
          onChange={(dates) => setStartDate(dates[0])}
          disabled={dateChangeDisabled}
          options={{
            enableTime: false,
            minDate: 'today',
            formatDate: (date) => intl.formatDate(date),
            locale: { firstDayOfWeek: 1 },
          }}
        />
      </Col>
      <Col className="pl-50">
        <TooltipLabel
          for="time-picker"
          className="mb-n25"
          labelClassName="text-secondary"
          tooltip={
            isClientReservation
              ? messages('add_reservation_client_select_time_tooltip')
              : messages('add_reservation_business_select_time_tooltip')
          }
        >
          {messages('label_time')}
        </TooltipLabel>
        <TimeSelector
          id="time-picker"
          value={startTime}
          isDisabled={areTimeslotsDisabled}
          timeslots={filteredTimeslots}
          isLoading={areTimeslotsLoading}
          onChange={(option) => setStartTime(option?.value)}
          menuPlacement={menuPlacement}
        />
      </Col>
    </Row>
  );
};
